import { inject, Injectable, Injector } from '@angular/core';
import { ZIMCallInvitationReceivedEventResult } from 'libs/zego-zim-web/entity';

import { LocalNotificationService } from '../../../services/notification.service';
import { ZimService } from '../../../services/zim.service';
import { safeJsonParse } from '../../../utils/utils';
import { CALL_END_REASON, CALL_INCOMING_STATUS, CALL_STATE, CALL_TYPE } from '../constants';
import { CallInvitation, PayloadDict } from '../types/call';
import { isSecretConversation } from './../../../utils/utils';
import { CallAudioService } from './call-audio.service';
import { CallSessionStoreService } from './call-session-store.service';
import { CallStoreService } from './call-store.service';
import { CallUserResolverService } from './call-user-resolver.service';
import { CallZimService } from './call-zim.service';
import { CallZimUserStateHandlerService } from './call-zim-user-state-handler.service';
import { PipService } from './pip.service';

@Injectable({
  providedIn: 'root',
})
export class CallZimEventHandlerService {
  private readonly callZimService = inject(CallZimService);
  private readonly participantResolver = inject(CallUserResolverService);
  private readonly callZimUserStateHandler = inject(CallZimUserStateHandlerService);
  private readonly callSessionStore = inject(CallSessionStoreService);
  private readonly callAudio = inject(CallAudioService);
  private readonly zimService = inject(ZimService);
  private readonly injector = inject(Injector);
  private readonly pipService = inject(PipService);
  private readonly localNotificationService = inject(LocalNotificationService);

  private _callStore: CallStoreService | null = null;
  private get callStore(): CallStoreService {
    if (!this._callStore) {
      this._callStore = this.injector.get(CallStoreService);
    }
    return this._callStore;
  }

  private getInviter(data: ZIMCallInvitationReceivedEventResult, extendedData: PayloadDict | null) {
    if (!data) {
      return '';
    }

    if (data.inviter) {
      return data.inviter;
    }

    if (data.caller) {
      return data.caller;
    }

    if (extendedData?.phoneCaller) {
      return extendedData.phoneCaller;
    }

    return '';
  }

  setupEventListeners(): void {
    // Lắng nghe nhận cuộc gọi đến
    this.callZimService.listenCallInvitationReceived(async (zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallInvitationReceived', JSON.stringify(data));

      const extendedData: PayloadDict | null = safeJsonParse(data.extendedData);
      if (!extendedData) return;
      if (extendedData.groupId || isSecretConversation(data.callID)) return;
      const currentPending = this.callStore.pendingInvitation();

      if (currentPending) {
        if (currentPending.callId === data.callID) return;

        console.log('[DEBUG-CALL]', 'rejectBusy on listenCallInvitationReceived');
        await this.callStore.rejectBusy(
          extendedData.isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
          data.callID,
        );
        this.callSessionStore.setPendingInvitation(null);
        return;
      }

      const participants = await this.participantResolver.resolveParticipants(
        data.callUserList,
        extendedData,
        'Received',
      );
      const invitation: CallInvitation = {
        callId: data.callID,
        inviter: this.getInviter(data, extendedData),
        invitees: participants,
        type: !extendedData.isVideoCall ? CALL_TYPE.AUDIO : CALL_TYPE.VIDEO,
        createTime: data.createTime ? new Date(data.createTime) : new Date(),
        timeoutSeconds: data.timeout ?? 60,
        receivedAt: new Date(),
        status: CALL_INCOMING_STATUS.INCOMING,
        extendedData,
      };
      const currentCall = this.callStore.currentCall();
      this.callSessionStore.beforeCallType.set(
        !extendedData.isVideoCall ? CALL_TYPE.AUDIO : CALL_TYPE.VIDEO,
      );

      // đang trong cuộc gọi với user A mà user A gọi lại từ thiết bị khác
      // (cùng remote user 1-1, khác callId) -> end lặng cuộc cũ và thay bằng incoming mới.
      if (currentCall && currentCall.callId !== invitation.callId) {
        const selfId = this.zimService.userId;
        const currentRemoteIds = this.getRemoteIds1to1(
          currentCall.extendedData?.callUserList,
          this.callStore.participants().map((p) => p.userId),
          selfId,
        );
        const newRemoteIds = this.getRemoteIds1to1(
          extendedData.callUserList,
          participants.map((p) => p.userId),
          selfId,
        );
        if (
          currentRemoteIds.length === 1 &&
          newRemoteIds.length === 1 &&
          currentRemoteIds[0] === newRemoteIds[0]
        ) {
          await this.replaceSameUserCall(
            currentCall.callId,
            data.callID,
            participants,
            extendedData,
            invitation,
          );
          return;
        }
      }

      switch (this.callStore.callState()) {
        case CALL_STATE.OUTGOING:
        case CALL_STATE.RINGING:
        case CALL_STATE.CONNECTING:
          if (currentCall) {
            console.log(
              '[DEBUG-CALL]',
              'rejectBusy when have current call 1',
              this.callStore.callState(),
            );
            await this.callStore.rejectBusy(invitation.type, invitation.callId);
          } else {
            this.callStore.createNewInvitation(
              'Received',
              data.callID,
              participants,
              extendedData,
              invitation,
            );
          }
          return;
        case CALL_STATE.CONNECTED: {
          const pending = this.callStore.pendingInvitation();
          if (!pending) {
            invitation.status = CALL_INCOMING_STATUS.WAITING;
            this.callSessionStore.setPendingInvitation(invitation);
            if (!this.pipService.isOpen()) {
              this.callStore.isModalHidden.set(false);
            }
          } else {
            if (pending.callId !== invitation.callId) {
              console.log(
                '[DEBUG-CALL]',
                'rejectBusy when have current call 2',
                this.callStore.callState(),
              );
              await this.callStore.rejectBusy(invitation.type, invitation.callId);
            }
          }
          return;
        }
        case CALL_STATE.ENDED:
          this.callStore.reset();
          this.callStore.createNewInvitation(
            'Received',
            data.callID,
            participants,
            extendedData,
            invitation,
          );
          return;
        default:
          this.callStore.createNewInvitation(
            'Received',
            data.callID,
            participants,
            extendedData,
            invitation,
          );
          return;
      }
    });

    // Lắng nghe khởi tạo cuộc gọi
    this.callZimService.listenCallInvitationCreated(async (zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallInvitationCreated', JSON.stringify(data));

      // kiểm tra có phải mình gọi hay không
      if (!this.callStore.isMeClickCall()) return;
      this.callStore.isMeClickCall.set(false);
      const extendedData: PayloadDict | null = safeJsonParse(data.extendedData);
      if (!extendedData) return;
      const participants = await this.participantResolver.resolveParticipants(
        data.callUserList,
        extendedData,
        'Created',
      );

      this.callStore.createNewInvitation('Created', data.callID, participants, extendedData);
      this.callSessionStore.beforeCallType.set(
        extendedData.isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
      );
    });

    // Trạng thái người dùng khi nghe cuộc gọi đã thay đổi.
    this.callZimService.listenCallUserStateChanged(async (zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallUserStateChanged', JSON.stringify(data));

      // Cập nhật trạng thái của các thành viên tham gia.
      this.callSessionStore.updateParticipants((participants) =>
        participants.map((p) => {
          const userState = data.callUserList.find((u) => u.userID === p.userId);
          if (userState) {
            return { ...p, state: userState.state };
          }
          return p;
        }),
      );

      // Xử lý theo trạng thái
      for (const user of data.callUserList) {
        const selfUser = user.userID === this.zimService.userId;
        await this.callZimUserStateHandler.handle(user, selfUser, data);
      }
    });

    // Lắng nghe thông báo lời mời gọi bị hủy
    this.callZimService.listenCallInvitationCancelled((zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallInvitationCancelled', data);
      this.callStore.showMissingCallNotification(data);
      this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.CANCELLED);
    });

    // Lắng nghe thông báo hết thời gian chờ lời mời gọi
    this.callZimService.listenCallInvitationTimeout((zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallInvitationTimeout', data);
      this.callStore.showMissingCallNotification(data);
      this.callAudio.stop();
      this.callStore.handleCallInvitationError(
        data.callID,
        this.callStore.resolveTimeoutEndReason(),
      );
    });

    // Lắng nghe cuộc gọi kết thúc
    this.callZimService.listenCallInvitationEnded((zim, data) => {
      console.log('[DEBUG-CALL]', 'listenCallInvitationEnded', data);
      if (this.callStore.isSwitchCall(data.extendedData)) return;
      this.callStore.handleCallInvitationError(data.callID, CALL_END_REASON.ENDED);
    });
  }

  /** Lấy danh sách remote user (loại trừ self). Ưu tiên callUserList, fallback participants đã resolve. */
  private getRemoteIds1to1(
    callUserList: string[] | undefined,
    resolvedIds: string[],
    selfId: string | null,
  ): string[] {
    const fromList = (callUserList ?? []).filter((id) => id && id !== selfId);
    if (fromList.length > 0) return fromList;
    return (resolvedIds ?? []).filter((id) => id && id !== selfId);
  }

  /**
   * end lặng cuộc gọi cũ với cùng remote user 1-1 và thay bằng modal incoming mới.
   * Auto-replace im lặng, không rejectBusy cuộc mới, không toast.
   */
  private async replaceSameUserCall(
    oldCallId: string,
    newCallId: string,
    participants: CallInvitation['invitees'],
    extendedData: PayloadDict,
    invitation: CallInvitation,
  ): Promise<void> {
    console.log('[DEBUG-CALL]', 'replaceSameUserCall', JSON.stringify({ oldCallId, newCallId }));
    const oldCall = this.callStore.currentCall();
    const state = this.callStore.callState();

    try {
      if (oldCall) {
        if (
          (state === CALL_STATE.OUTGOING || state === CALL_STATE.RINGING) &&
          oldCall.inviter === this.zimService.userId
        ) {
          const invitees = this.callStore.participants().map((p) => p.userId);
          await this.callStore.callZimService.callCancel(invitees, oldCallId, {
            extendedData: '',
          });
        } else if (oldCall.inviter === this.zimService.userId) {
          await this.callStore.callZimService.callEnd(oldCallId, { extendedData: '' });
        } else {
          await this.callStore.callZimService.callQuit(oldCallId, { extendedData: '' });
        }
      }
    } catch {
      // End lặng: bỏ qua lỗi SDK khi dọn cuộc cũ.
    }

    try {
      if (this.callStore.isLoginRoom()) {
        this.callStore.callZegoService.logoutRoom(oldCallId);
        this.callStore.isLoginRoom.set(false);
      }
    } catch {
      // Bỏ qua lỗi logoutRoom.
    }

    try {
      const localStream = this.callStore.localStream();
      const localStreamId = this.callStore.localStreamId();
      if (localStream && localStreamId) {
        this.callStore.callZegoService.stopPubishAndDestroyStream(localStream, localStreamId);
      }
    } catch {
      // Bỏ qua lỗi destroy stream.
    }

    try {
      if (this.callStore.zegoMediaControlService.mediaState().isScreenSharing) {
        this.callStore.zegoMediaControlService.stopScreenShare();
      }
    } catch {
      // Bỏ qua lỗi stop share.
    }

    try {
      this.localNotificationService.clearCallAlert(oldCallId);
    } catch {
      // Bỏ qua lỗi notification.
    }

    // Xóa cuộc cũ rồi dựng incoming mới ở trạng thái INCOMING (đổ chuông, không WAITING).
    this.callStore.reset();
    this.callSessionStore.beforeCallType.set(
      !extendedData.isVideoCall ? CALL_TYPE.AUDIO : CALL_TYPE.VIDEO,
    );
    this.callStore.createNewInvitation(
      'Received',
      newCallId,
      participants,
      extendedData,
      invitation,
    );
  }
}
